import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface CurrencyInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
  className?: string;
  id?: string;
  name?: string;
}

/**
 * CurrencyInput - Specialized input for currency values
 * Handles formatting and validation for currency input
 */
const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ 
    value, 
    onChange, 
    onBlur,
    onFocus,
    placeholder = "0.00", 
    disabled = false, 
    error, 
    label, 
    required = false,
    className,
    id,
    name,
    ...props 
  }, ref) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let inputValue = e.target.value;
      
      // Remove any non-digit characters except decimal point
      inputValue = inputValue.replace(/[^0-9.]/g, '');
      
      // Ensure only one decimal point
      const parts = inputValue.split('.');
      if (parts.length > 2) {
        inputValue = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Limit decimal places to 2
      if (parts[1] && parts[1].length > 2) {
        inputValue = parts[0] + '.' + parts[1].substring(0, 2);
      }
      
      // Prevent leading zeros except for decimal values
      if (inputValue.length > 1 && inputValue[0] === '0' && inputValue[1] !== '.') {
        inputValue = inputValue.substring(1);
      }
      
      onChange(inputValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter
      if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Allow: home, end, left, right
        (e.keyCode >= 35 && e.keyCode <= 39)) {
        return;
      }
      
      // Ensure that it is a number or decimal point and stop the keypress
      if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && 
          (e.keyCode < 96 || e.keyCode > 105) && 
          e.keyCode !== 190 && e.keyCode !== 110) {
        e.preventDefault();
      }
    };

    const inputId = id || name || 'currency-input';

    return (
      <div className={cn("relative", className)}>
        {label && (
          <label 
            htmlFor={inputId}
            className={cn(
              "block text-sm font-medium text-gray-700 mb-2",
              required && "required"
            )}
          >
            {label}
          </label>
        )}
        
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            name={name}
            type="text"
            inputMode="decimal"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            onFocus={onFocus}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              // Base styles
              "block w-full h-10 px-3 text-base",
              "border border-gray-300 rounded-md",
              "bg-white text-gray-900 placeholder-gray-400",
              "transition-colors duration-200",
              
              // Focus styles
              "focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary",
              
              // Disabled styles
              disabled && "bg-gray-50 text-gray-500 cursor-not-allowed",
              
              // Error styles
              error && "border-red-300 focus:border-apple-red focus:ring-apple-red"
            )}
            {...props}
          />
        </div>
        
        {/* Error Message - Overlay style like Input component */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="absolute top-full left-0 right-0 mt-1 z-10"
          >
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 shadow-sm">
              <p className="text-sm text-red-600 font-medium">{error}</p>
            </div>
          </motion.div>
        )}
      </div>
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';

export { CurrencyInput };
